// RoomSheet — the room `⋯` action sheet on mobile. Presentational: the
// parent owns the Matrix verbs. The default view lists the room header,
// a notification radio group, Members, Archive, and Leave; Members swaps
// the body for the reused MemberList inside the same dialog.

import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { MemberList } from "~/components/MemberList";
import { NotificationLevelOptions } from "~/components/NotificationSettings";
import { mobileStrings } from "~/components/mobile/strings";
import type { RoomNotificationLevel } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export interface RoomSheetProps {
  roomId: string;
  /** Display label as the header shows it (e.g. `#design` or a DM name). */
  roomLabel: string;
  memberCount: number;
  notificationLevel: RoomNotificationLevel;
  onSelectNotificationLevel: (level: RoomNotificationLevel) => void;
  onArchive: () => void;
  onLeave: () => void;
  /** Block the other person; only passed for 1:1 DMs. */
  onBlock?: () => void;
  /** Close request — Escape, back, backdrop, or after a terminal action. */
  onClose: () => void;
}

const PANEL_STYLE: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

const MEMBERS_PANEL_STYLE: CSSProperties = {
  ...PANEL_STYLE,
  height: "70vh",
};

export function RoomSheet({
  roomId,
  roomLabel,
  memberCount,
  notificationLevel,
  onSelectNotificationLevel,
  onArchive,
  onLeave,
  onBlock,
  onClose,
}: RoomSheetProps) {
  const titleId = useId();
  const [view, setView] = useState<"main" | "members">("main");
  const backRef = useRef<HTMLButtonElement>(null);
  const membersRowRef = useRef<HTMLButtonElement>(null);
  const prevViewRef = useRef(view);

  // The two views share one Dialog fiber, so Dialog's mount-time focus
  // does not re-run on a view swap; move focus into the new view here.
  // Guarding on the previous view (not a mount latch) only fires on an
  // actual change, so it survives StrictMode's double effect invoke and
  // leaves the initial focus to Dialog.
  useEffect(() => {
    if (prevViewRef.current === view) return;
    prevViewRef.current = view;
    if (view === "members") backRef.current?.focus();
    else membersRowRef.current?.focus();
  }, [view]);

  if (view === "members") {
    return (
      <Dialog
        onClose={onClose}
        align="bottom"
        labelledBy={titleId}
        panelStyle={MEMBERS_PANEL_STYLE}
      >
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-1)",
              padding: "var(--chat-space-2) var(--chat-space-3)",
              borderBottom: "1px solid var(--chat-surface-border)",
            }}
          >
            <button
              ref={backRef}
              type="button"
              onClick={() => setView("main")}
              aria-label={mobileStrings.nav.back}
              className="chat-button outline-none focus-visible:shadow-neon-cyan"
              style={{
                width: 44,
                height: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: "var(--chat-radius-md)",
                background: "transparent",
                color: "var(--chat-fg-secondary)",
                cursor: "pointer",
              }}
            >
              <ChevronLeft size={20} strokeWidth={ICON_STROKE} />
            </button>
            <h2
              id={titleId}
              style={{
                margin: 0,
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontWeight: 300,
                fontSize: "var(--chat-type-body-lg-size)",
                lineHeight: "var(--chat-type-body-lg-line)",
              }}
            >
              {mobileStrings.roomSheet.members}
            </h2>
          </div>
          <MemberList roomId={roomId} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog onClose={onClose} align="bottom" labelledBy={titleId} panelStyle={PANEL_STYLE}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <header
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "var(--chat-space-2) var(--chat-space-4) var(--chat-space-4)",
          }}
        >
          <h2
            id={titleId}
            style={{
              margin: 0,
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontWeight: 300,
              fontSize: "var(--chat-type-body-lg-size)",
              lineHeight: "var(--chat-type-body-lg-line)",
            }}
          >
            {roomLabel}
          </h2>
          <span
            style={{
              color: "var(--chat-fg-muted)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {mobileStrings.room.members(memberCount)}
          </span>
        </header>
        <section
          aria-label={mobileStrings.roomSheet.notifications}
          style={{ paddingBottom: "var(--chat-space-4)" }}
        >
          <NotificationLevelOptions
            variant="sheet"
            level={notificationLevel}
            groupLabel={mobileStrings.roomSheet.notifications}
            onSelect={onSelectNotificationLevel}
          />
        </section>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: "var(--chat-space-2)",
          }}
        >
          <SheetRow
            buttonRef={membersRowRef}
            onClick={() => setView("members")}
            trailing={
              <ChevronRight
                size={18}
                strokeWidth={ICON_STROKE}
                aria-hidden
                style={{ color: "var(--chat-fg-muted)", flexShrink: 0 }}
              />
            }
          >
            {mobileStrings.roomSheet.members}
          </SheetRow>
          <SheetRow onClick={onArchive}>{mobileStrings.roomSheet.archive}</SheetRow>
          {onBlock && (
            <SheetRow destructive onClick={onBlock}>
              {mobileStrings.roomSheet.block(roomLabel)}
            </SheetRow>
          )}
          <SheetRow destructive onClick={onLeave}>
            {mobileStrings.roomSheet.leave}
          </SheetRow>
        </div>
      </div>
    </Dialog>
  );
}

function SheetRow({
  children,
  onClick,
  destructive = false,
  trailing,
  buttonRef,
}: {
  children: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  trailing?: ReactNode;
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      className="chat-button outline-none focus-visible:shadow-neon-cyan"
      style={{
        minHeight: 52,
        display: "flex",
        alignItems: "center",
        width: "100%",
        textAlign: "left",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        padding: "0 var(--chat-space-4)",
        borderRadius: 0,
        fontFamily: "var(--chat-font-sans)",
        fontWeight: 300,
        fontSize: "var(--chat-type-body-lg-size)",
        lineHeight: "var(--chat-type-body-lg-line)",
        color: destructive ? "var(--chat-state-danger)" : "var(--chat-fg-primary)",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {trailing}
    </button>
  );
}
